<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { formatAddress, formatSimpleAddress } from '@/helpers/format';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { TrashIcon } from '@heroicons/vue/24/outline';

import OutgoingPropertyForm from './OutgoingPropertyForm.vue';
import SimpleOutgoingPropertyForm from './SimpleOutgoingPropertyForm.vue';

import type { OutgoingPropertyEntry } from '@/types/Property';

const props = withDefaults(
  defineProps<{
    modelValue: OutgoingPropertyEntry;
    isMultiple?: boolean;
  }>(),
  {
    isMultiple: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: OutgoingPropertyEntry): void;
  (e: 'remove', id: string): void;
}>();

const { sectionStates } = useUserQuoteProcess();

const showModal = ref(false);
const inputVal = ref<OutgoingPropertyEntry>(props.modelValue);
const newItem = ref(!props.modelValue.address);
const showEntry = ref(!props.modelValue.address);
const simpleFormRef = ref<InstanceType<typeof SimpleOutgoingPropertyForm>>();
const comprehensiveFormRef = ref<InstanceType<typeof OutgoingPropertyForm>>();

const outgoingPropertiesType = computed(() => sectionStates.value.outgoingProperties);

const address = computed(() =>
  props.modelValue.address ? formatAddress(props.modelValue.address) : 'the new property',
);

const simpleAddress = computed(() =>
  props.modelValue.address ? formatSimpleAddress(props.modelValue.address) : 'the new property',
);

const isSectionSimple = computed(() => outgoingPropertiesType.value === 'simple');

const editProperty = () => {
  showEntry.value = true;
};

const update = async () => {
  const activeForm = isSectionSimple.value ? simpleFormRef.value : comprehensiveFormRef.value;

  const saved = (await activeForm?.update()) ?? false;

  if (saved) {
    newItem.value = false;
    showEntry.value = false;

    emit('update:modelValue', inputVal.value);
  }

  forceScrollToTop(0);

  return saved;
};

const cancel = () => {
  if (newItem.value) {
    removeItem();
  } else {
    const activeForm = isSectionSimple.value ? simpleFormRef.value : comprehensiveFormRef.value;

    activeForm?.cancel();
    showEntry.value = false;
  }
  forceScrollToTop(0);
};

const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.id);
};

watch(
  () => props.modelValue,
  (val) => {
    inputVal.value = val;
    newItem.value = !props.modelValue.address;
    showEntry.value = !props.modelValue.address;
  },
  { immediate: true },
);

watch(
  () => props.isMultiple,
  (val) => {
    showEntry.value = !val || newItem.value;
  },
  { immediate: true },
);

const handleModeSwitch = (newMode: 'simple' | 'comprehensive') => {
  const formToSync = newMode === 'simple' ? comprehensiveFormRef.value : simpleFormRef.value;

  formToSync?.syncState();

  emit('update:modelValue', inputVal.value);
};

defineExpose({ update, handleModeSwitch });
</script>

<template>
  <div>
    <BxExpander v-model="showEntry" separator>
      <template #header>
        <div class="px-4 py-5 sm:px-6">
          <template v-if="newItem">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                <span class="mr-3 hidden md:block"><BxIcon name="outgoingProperty" /></span>
                <span>Property details</span>
              </h3>
            </div>
          </template>
          <template v-else-if="showEntry">
            <h3 class="text-lg font-medium leading-6 text-gray-900">Edit property</h3>
          </template>
          <template v-else>
            <div class="-ml-4 -mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div v-if="isSectionSimple" class="ml-4 mt-4">
                <p class="mt-1 text-sm text-gray-500">
                  {{ simpleAddress }}
                </p>
              </div>
              <div v-else class="ml-4 mt-4">
                <h3 class="text-lg font-medium leading-6 text-gray-900">
                  {{ [modelValue.address?.addressLine, modelValue.address?.addressLine2].filter((x) => x).join(', ') }}
                </h3>
                <p class="mt-1 text-sm text-gray-500">
                  {{ modelValue.address?.locality }}, {{ modelValue.address?.state }}
                  {{ modelValue.address?.postcode }}
                </p>
              </div>
              <div class="ml-4 mt-4 flex items-center gap-4">
                <BxButton
                  :data-test-id="`outgoing-property-${inputVal.id}-edit`"
                  variant="secondary"
                  @click="editProperty()"
                >
                  Edit
                </BxButton>
                <BxButton
                  v-if="isMultiple"
                  :data-test-id="`outgoing-property-${inputVal.id}-remove`"
                  variant="link-danger"
                  class="inline-flex items-center gap-2"
                  @click="showModal = true"
                >
                  <TrashIcon class="mr-1 h-5 w-5" aria-hidden="true" />
                  <span>Remove</span>
                </BxButton>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template #body>
        <div class="p-6">
          <SimpleOutgoingPropertyForm
            v-show="isSectionSimple"
            ref="simpleFormRef"
            :model-value="inputVal"
            @update:model-value="inputVal = $event"
          />
          <OutgoingPropertyForm
            v-show="!isSectionSimple"
            ref="comprehensiveFormRef"
            :model-value="inputVal"
            @update:model-value="inputVal = $event"
          />
          <div v-if="isMultiple" class="mt-10 flex-shrink-0">
            <BxButton
              :id="inputVal.id + '-save'"
              :data-test-id="`outgoing-property-${inputVal.id}-save`"
              variant="secondary"
              @click="update()"
            >
              Save
            </BxButton>
            <BxButton
              :data-test-id="`outgoing-property-${inputVal.id}-cancel`"
              variant="link"
              class="ml-4"
              @click="cancel()"
            >
              Cancel
            </BxButton>
          </div>
        </div>
      </template>
    </BxExpander>
    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      :data-test-id="`outgoing-property-${inputVal.id}-remove-modal`"
      title="Remove property"
      confirm-label="Remove"
      cancel-label="Cancel"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ isSectionSimple ? simpleAddress : address }}</span
      >?
    </BxModal>
  </div>
</template>
